import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Shield, Upload, X, Calendar, AlertCircle, 
  FileText, User, Building2, Phone, Mail, ArrowLeft, Check 
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import AddressAutocomplete from '../ui/AddressAutocomplete';
import { Label } from '../ui/label';
import { BulkFileUploader } from './BulkFileUploader';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';

const NewClaim = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [estimates, setEstimates] = useState([]);
  const [selectedEstimate, setSelectedEstimate] = useState(null);
  const [uploadedPhotos, setUploadedPhotos] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [isDirectClaim, setIsDirectClaim] = useState(false); // Toggle for direct claim (no estimate)

  // Use window.location.origin to ensure HTTPS is used when page is loaded over HTTPS
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  
  // Debug: Log the backend URL being used
  console.log('NewClaim: BACKEND_URL =', BACKEND_URL);
  console.log('NewClaim: process.env.REACT_APP_BACKEND_URL =', process.env.REACT_APP_BACKEND_URL);
  console.log('NewClaim: window.location.origin =', window.location.origin);

  // Form state
  const [formData, setFormData] = useState({
    estimate_id: '',
    insurance_company: '',
    claim_number: '',
    policy_number: '',
    adjuster_name: '',
    adjuster_email: '',
    adjuster_phone: '',
    adjuster_manager_name: '',
    adjuster_manager_email: '',
    adjuster_manager_phone: '',
    customer_first_name: '',
    customer_last_name: '',
    customer_email: '',
    customer_phone: '',
    property_address: '', // For direct claims
    storm_date: '',
    inspection_date: '',
    damage_percentage: '',
    damage_description: '',
    damage_types: []
  });

  const [errors, setErrors] = useState({});

  // Damage types available
  const damageTypeOptions = [
    { value: 'hail', label: 'Hail Damage' },
    { value: 'wind', label: 'Wind Damage' },
    { value: 'water', label: 'Water Damage' },
    { value: 'fire', label: 'Fire Damage' },
    { value: 'other', label: 'Other' }
  ];

  useEffect(() => {
    if (user?.id) {
      loadEstimates();
    }
  }, [user?.id]);

  // Auto-populate from URL query params (when coming from a CRM lead card)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const updates = {};
    
    // Helper: only use param if it's a real value
    const getParam = (key) => {
      const v = params.get(key);
      return (v && v !== 'None' && v !== 'null' && v !== 'undefined') ? v : '';
    };
    
    if (getParam('customer_first_name')) updates.customer_first_name = getParam('customer_first_name');
    if (getParam('customer_last_name')) updates.customer_last_name = getParam('customer_last_name');
    if (getParam('customer_email')) updates.customer_email = getParam('customer_email');
    if (getParam('customer_phone')) updates.customer_phone = getParam('customer_phone');
    if (getParam('property_address')) updates.property_address = getParam('property_address');
    if (getParam('estimate_id')) updates.estimate_id = getParam('estimate_id');
    
    if (Object.keys(updates).length > 0) {
      setFormData(prev => ({ ...prev, ...updates }));
      if (updates.property_address && !updates.estimate_id) {
        setIsDirectClaim(true);
      }
    }
    
    // Load lead files for preview
    const leadId = getParam('lead_id') || getParam('estimate_id');
    if (leadId) {
      loadLeadFiles(leadId);
    }
  }, []);

  const [leadFiles, setLeadFiles] = useState([]);
  
  const loadLeadFiles = async (leadId) => {
    try {
      const token = localStorage.getItem('rooftrack_token');
      const res = await axios.get(`${BACKEND_URL}/api/crm/leads/${leadId}/files`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const files = Array.isArray(res.data) ? res.data : (res.data.files || []);
      setLeadFiles(files);
    } catch (err) {
      console.log('No lead files found:', err.message);
    }
  };

  // When estimates load and we have an estimate_id from URL, auto-select it
  useEffect(() => {
    if (estimates.length > 0 && formData.estimate_id) {
      const match = estimates.find(est => est.id === formData.estimate_id);
      if (match) {
        setSelectedEstimate(match);
        // Helper to get a real value (not "None" string)
        const real = (v) => (v && v !== 'None' && v !== 'null') ? v : '';
        // Fill customer info from the estimate if not already set
        setFormData(prev => ({
          ...prev,
          customer_first_name: prev.customer_first_name || real(match.first_name) || real(match.customer_first_name) || '',
          customer_last_name: prev.customer_last_name || real(match.last_name) || real(match.customer_last_name) || '',
          customer_email: prev.customer_email || real(match.email) || real(match.customer_email) || '',
          customer_phone: prev.customer_phone || real(match.phone) || real(match.customer_phone) || '',
          property_address: prev.property_address || real(match.address) || '',
        }));
      }
    }
  }, [estimates, formData.estimate_id]);

  const loadEstimates = async () => {
    if (!user?.id) {
      console.log('NewClaim: User ID not available yet');
      return;
    }
    
    console.log('NewClaim: Loading estimates for user:', user.id);
    console.log('NewClaim: Using BACKEND_URL:', BACKEND_URL);
    setIsLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/estimates/`, {
        params: { user_id: user.id }
      });
      console.log('NewClaim: API Response received, estimates count:', response.data.length);
      console.log('NewClaim: First 3 estimates:', response.data.slice(0, 3).map(e => ({
        id: e.id,
        address: e.address
      })));
      setEstimates(response.data);
    } catch (error) {
      console.error('NewClaim: Error loading estimates:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEstimateChange = (e) => {
    const estimateId = e.target.value;
    const estimate = estimates.find(est => est.id === estimateId);
    
    setFormData(prev => ({ ...prev, estimate_id: estimateId }));
    setSelectedEstimate(estimate);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleDamageTypeToggle = (damageType) => {
    setFormData(prev => {
      const currentTypes = prev.damage_types || [];
      const isSelected = currentTypes.includes(damageType);
      
      return {
        ...prev,
        damage_types: isSelected
          ? currentTypes.filter(t => t !== damageType)
          : [...currentTypes, damageType]
      };
    });
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    for (const file of files) {
      const fileId = `${Date.now()}-${file.name}`;
      setUploadProgress(prev => ({ ...prev, [fileId]: 0 }));

      try {
        const formData = new FormData();
        formData.append('file', file);

        // Upload to Supabase via backend
        const response = await axios.post(
          `${BACKEND_URL}/api/claims/upload-photo`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data'
            },
            onUploadProgress: (progressEvent) => {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              setUploadProgress(prev => ({ ...prev, [fileId]: percentCompleted }));
            }
          }
        );

        setUploadedPhotos(prev => [
          ...prev,
          {
            id: fileId,
            file_name: file.name,
            file_size: file.size,
            supabase_url: response.data.url,
            supabase_path: response.data.path
          }
        ]);

        // Remove from progress
        setUploadProgress(prev => {
          const newProgress = { ...prev };
          delete newProgress[fileId];
          return newProgress;
        });

      } catch (error) {
        console.error('Error uploading photo:', error);
        alert(`Failed to upload ${file.name}. Please try again.\n\nError: ${error.response?.data?.detail || error.message}`);
        
        setUploadProgress(prev => {
          const newProgress = { ...prev };
          delete newProgress[fileId];
          return newProgress;
        });
      }
    }
  };

  const removePhoto = (photoId) => {
    setUploadedPhotos(prev => prev.filter(photo => photo.id !== photoId));
  };

  const validateForm = () => {
    const newErrors = {};

    if (isDirectClaim) {
      // Direct claim validations
      if (!formData.property_address.trim()) {
        newErrors.property_address = 'Property address is required';
      }
      if (!formData.customer_last_name.trim()) {
        newErrors.customer_last_name = 'Customer last name is required';
      }
    } else {
      // Estimate-based claim validations
      if (!formData.estimate_id) {
        newErrors.estimate_id = 'Please select an estimate';
      }
      if (formData.damage_types.length === 0) {
        newErrors.damage_types = 'Please select at least one damage type';
      }
    }

    if (!formData.insurance_company.trim()) {
      newErrors.insurance_company = 'Insurance company is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      let response;
      
      if (isDirectClaim) {
        // Direct claim (no estimate)
        const directClaimData = {
          property_address: formData.property_address,
          customer_name: `${formData.customer_first_name} ${formData.customer_last_name}`.trim(),
          policy_number: formData.policy_number,
          claim_number: formData.claim_number,
          insurance_company: formData.insurance_company || undefined,
          customer_email: formData.customer_email || undefined,
          customer_phone: formData.customer_phone || undefined,
          storm_date: formData.storm_date ? new Date(formData.storm_date).toISOString() : undefined
        };
        
        response = await axios.post(
          `${BACKEND_URL}/api/claims/direct`,
          directClaimData,
          {
            params: { user_id: user.id }
          }
        );
      } else {
        // Estimate-based claim
        const claimData = {
          estimate_id: formData.estimate_id,
          insurance_company: formData.insurance_company,
          claim_number: formData.claim_number || undefined,
          policy_number: formData.policy_number || undefined,
          storm_date: formData.storm_date ? new Date(formData.storm_date).toISOString() : undefined,
          inspection_date: formData.inspection_date ? new Date(formData.inspection_date).toISOString() : undefined,
          damage_types: formData.damage_types,
          damage_percentage: formData.damage_percentage ? parseFloat(formData.damage_percentage) : undefined,
          damage_description: formData.damage_description || undefined
        };

        // Add customer contact info if provided
        if (formData.customer_first_name || formData.customer_last_name || formData.customer_email || formData.customer_phone) {
          claimData.customer_first_name = formData.customer_first_name || undefined;
          claimData.customer_last_name = formData.customer_last_name || undefined;
          claimData.customer_email = formData.customer_email || undefined;
          claimData.customer_phone = formData.customer_phone || undefined;
        }

        // Add adjuster info if provided (including escalation contacts)
        if (formData.adjuster_name || formData.adjuster_email || formData.adjuster_phone) {
          claimData.adjuster = {
            name: formData.adjuster_name || undefined,
            email: formData.adjuster_email || undefined,
            phone: formData.adjuster_phone || undefined,
            manager_name: formData.adjuster_manager_name || undefined,
            manager_email: formData.adjuster_manager_email || undefined,
            manager_phone: formData.adjuster_manager_phone || undefined
          };
        }

        response = await axios.post(
          `${BACKEND_URL}/api/claims`,
          claimData,
          {
            params: { user_id: user.id }
          }
        );
      }

      const claimId = response.data.id;

      // Auto-attach lead files (photos, videos, documents) to the claim
      const leadId = formData.estimate_id || new URLSearchParams(window.location.search).get('lead_id');
      if (leadId) {
        try {
          await axios.post(
            `${BACKEND_URL}/api/claims/${claimId}/attach-lead-files`,
            { lead_id: leadId },
            { params: { user_id: user.id } }
          );
        } catch (attachErr) {
          console.error('Auto-attach lead files failed:', attachErr);
        }
      }

      // Upload policy files if selected
      const policyFiles = formData._policyFiles || (formData._policyFile ? [formData._policyFile] : []);
      for (const pFile of policyFiles) {
        try {
          const policyForm = new FormData();
          policyForm.append('file', pFile);
          await axios.post(
            `${BACKEND_URL}/api/claims/${claimId}/upload-policy?user_id=${user.id}`,
            policyForm,
            { headers: { 'Content-Type': 'multipart/form-data' } }
          );
        } catch (policyErr) {
          console.error('Policy upload failed:', policyErr);
        }
      }

      // Success - navigate to claim details
      navigate(`/dashboard/claims/${claimId}`);
      
    } catch (error) {
      console.error('Error creating claim:', error);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);
      
      let errorMessage = 'Failed to create claim. Please try again.';
      
      if (error.response?.data?.detail) {
        // Handle both string and object details
        if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else if (typeof error.response.data.detail === 'object') {
          errorMessage = JSON.stringify(error.response.data.detail, null, 2);
        }
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data) {
        // Try to stringify the entire data object
        errorMessage = JSON.stringify(error.response.data, null, 2);
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      alert(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading estimates...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/dashboard/claims')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Claims
        </button>
        
        <h1 className="text-3xl font-bold text-gray-900 flex items-center">
          <Shield className="w-8 h-8 mr-3 text-orange-600" />
          Create New Insurance Claim
        </h1>
        <p className="text-gray-600 mt-1">
          Submit a new insurance claim for roof damage assessment
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Claim Type Toggle */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <FileText className="w-5 h-5 mr-2 text-orange-600" />
            Claim Type
          </h2>
          
          <div className="flex gap-4">
            <label 
              className={`flex-1 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                !isDirectClaim 
                  ? 'border-orange-500 bg-orange-50' 
                  : 'border-gray-200 hover:border-orange-300'
              }`}
            >
              <input
                type="radio"
                name="claimType"
                checked={!isDirectClaim}
                onChange={() => setIsDirectClaim(false)}
                className="sr-only"
              />
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-gray-900">From Estimate</p>
                  <p className="text-sm text-gray-600">Link to an existing estimate with measurements</p>
                </div>
                {!isDirectClaim && <Check className="w-5 h-5 text-orange-600" />}
              </div>
            </label>
            
            <label 
              className={`flex-1 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                isDirectClaim 
                  ? 'border-orange-500 bg-orange-50' 
                  : 'border-gray-200 hover:border-orange-300'
              }`}
            >
              <input
                type="radio"
                name="claimType"
                checked={isDirectClaim}
                onChange={() => setIsDirectClaim(true)}
                className="sr-only"
              />
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-gray-900">Direct Claim</p>
                  <p className="text-sm text-gray-600">Create claim without an estimate</p>
                </div>
                {isDirectClaim && <Check className="w-5 h-5 text-orange-600" />}
              </div>
            </label>
          </div>
        </div>

        {/* Select Estimate - Only show when NOT direct claim */}
        {!isDirectClaim && (
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <FileText className="w-5 h-5 mr-2 text-orange-600" />
              Select Estimate
            </h2>

            <div>
              <Label htmlFor="estimate_id">Estimate *</Label>
              <select
                id="estimate_id"
                name="estimate_id"
                value={formData.estimate_id}
                onChange={handleEstimateChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                data-testid="estimate-select"
              >
                <option value="">Select an estimate...</option>
                {estimates.map(estimate => (
                  <option key={estimate.id} value={estimate.id}>
                    {estimate.address} - {formatCurrency(estimate.total_cost)} ({estimate.square_footage?.toFixed(0)} sq ft)
                  </option>
                ))}
              </select>
              {errors.estimate_id && (
                <p className="text-red-600 text-sm mt-1 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.estimate_id}
                </p>
              )}
            </div>

            {selectedEstimate && (
              <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                <h3 className="font-semibold text-blue-900 mb-2">Selected Property</h3>
                <p className="text-sm text-blue-800">{selectedEstimate.address}</p>
                <div className="grid grid-cols-3 gap-4 mt-2 text-sm">
                  <div>
                    <span className="text-blue-600">Square Footage:</span>
                    <p className="font-semibold text-blue-900">{selectedEstimate.square_footage?.toFixed(0)} sq ft</p>
                  </div>
                  <div>
                    <span className="text-blue-600">Estimate Total:</span>
                    <p className="font-semibold text-blue-900">{formatCurrency(selectedEstimate.total_cost)}</p>
                  </div>
                  <div>
                    <span className="text-blue-600">Roof Type:</span>
                    <p className="font-semibold text-blue-900 capitalize">{selectedEstimate.roof_type?.replace('_', ' ')}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Property Address - Only show for Direct Claim */}
        {isDirectClaim && (
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Building2 className="w-5 h-5 mr-2 text-orange-600" />
              Property Information
            </h2>

            <div>
              <Label htmlFor="property_address">Property Address *</Label>
              <AddressAutocomplete
                value={formData.property_address}
                onChange={(val) => setFormData(prev => ({...prev, property_address: val}))}
                onSelectAddress={(addr) => setFormData(prev => ({...prev, property_address: addr.display_name || addr}))}
                placeholder="Start typing an address..."
                data-testid="property-address-input"
              />
              {errors.property_address && (
                <p className="text-red-600 text-sm mt-1 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.property_address}
                </p>
              )}
            </div>
          </div>
        )}


        {/* Customer Information */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <User className="w-5 h-5 mr-2 text-orange-600" />
            Customer Information {isDirectClaim && '*'}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="customer_first_name">First Name</Label>
              <Input
                id="customer_first_name"
                name="customer_first_name"
                value={formData.customer_first_name}
                onChange={handleInputChange}
                placeholder="John"
                data-testid="customer-first-name-input"
              />
              {errors.customer_first_name && (
                <p className="text-red-600 text-sm mt-1 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.customer_first_name}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="customer_last_name">Last Name {isDirectClaim && '*'}</Label>
              <Input
                id="customer_last_name"
                name="customer_last_name"
                value={formData.customer_last_name}
                onChange={handleInputChange}
                placeholder="Smith"
                data-testid="customer-last-name-input"
              />
            </div>

            <div>
              <Label htmlFor="customer_email">Email</Label>
              <Input
                id="customer_email"
                name="customer_email"
                type="email"
                value={formData.customer_email}
                onChange={handleInputChange}
                placeholder="john.smith@email.com"
                data-testid="customer-email-input"
              />
            </div>

            <div>
              <Label htmlFor="customer_phone">Phone</Label>
              <Input
                id="customer_phone"
                name="customer_phone"
                type="tel"
                value={formData.customer_phone}
                onChange={handleInputChange}
                placeholder="(555) 123-4567"
                data-testid="customer-phone-input"
              />
            </div>
          </div>
        </div>

        {/* Insurance Information */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Building2 className="w-5 h-5 mr-2 text-orange-600" />
            Insurance Information
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="insurance_company">Insurance Company *</Label>
              <Input
                id="insurance_company"
                name="insurance_company"
                value={formData.insurance_company}
                onChange={handleInputChange}
                placeholder="e.g., State Farm, Allstate"
              />
              {errors.insurance_company && (
                <p className="text-red-600 text-sm mt-1 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.insurance_company}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="policy_number">Policy Number</Label>
              <Input
                id="policy_number"
                name="policy_number"
                value={formData.policy_number}
                onChange={handleInputChange}
                placeholder="Policy #"
                data-testid="policy-number-input"
              />
              {errors.policy_number && (
                <p className="text-red-600 text-sm mt-1 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.policy_number}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="claim_number">Claim Number</Label>
              <Input
                id="claim_number"
                name="claim_number"
                value={formData.claim_number}
                onChange={handleInputChange}
                placeholder="Claim #"
                data-testid="claim-number-input"
              />
              {errors.claim_number && (
                <p className="text-red-600 text-sm mt-1 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.claim_number}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Adjuster Information */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <User className="w-5 h-5 mr-2 text-orange-600" />
            Adjuster Information (Optional)
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="adjuster_name">Adjuster Name</Label>
              <Input
                id="adjuster_name"
                name="adjuster_name"
                value={formData.adjuster_name}
                onChange={handleInputChange}
                placeholder="Full name"
              />
            </div>

            <div>
              <Label htmlFor="adjuster_email">Adjuster Email</Label>
              <Input
                id="adjuster_email"
                name="adjuster_email"
                type="email"
                value={formData.adjuster_email}
                onChange={handleInputChange}
                placeholder="email@company.com"
              />
            </div>

            <div>
              <Label htmlFor="adjuster_phone">Adjuster Phone</Label>
              <Input
                id="adjuster_phone"
                name="adjuster_phone"
                type="tel"
                value={formData.adjuster_phone}
                onChange={handleInputChange}
                placeholder="(555) 123-4567"
              />
            </div>
          </div>

          {/* Escalation Contacts */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h3 className="text-md font-semibold text-gray-900 mb-4">Escalation Contacts (Optional)</h3>
            <p className="text-sm text-gray-600 mb-4">
              Adjuster's manager or supervisor for escalation purposes
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="adjuster_manager_name">Manager Name</Label>
                <Input
                  id="adjuster_manager_name"
                  name="adjuster_manager_name"
                  value={formData.adjuster_manager_name}
                  onChange={handleInputChange}
                  placeholder="Manager's full name"
                />
              </div>

              <div>
                <Label htmlFor="adjuster_manager_email">Manager Email</Label>
                <Input
                  id="adjuster_manager_email"
                  name="adjuster_manager_email"
                  type="email"
                  value={formData.adjuster_manager_email}
                  onChange={handleInputChange}
                  placeholder="manager@company.com"
                />
              </div>

              <div>
                <Label htmlFor="adjuster_manager_phone">Manager Phone</Label>
                <Input
                  id="adjuster_manager_phone"
                  name="adjuster_manager_phone"
                  type="tel"
                  value={formData.adjuster_manager_phone}
                  onChange={handleInputChange}
                  placeholder="(555) 999-8888"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Damage Information - Only show full section for estimate-based claims */}
        {!isDirectClaim && (
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <AlertCircle className="w-5 h-5 mr-2 text-orange-600" />
              Damage Information
            </h2>

            <div className="space-y-4">
              <div>
                <Label>Damage Types *</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-2">
                  {damageTypeOptions.map(option => (
                    <label
                      key={option.value}
                      className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                        formData.damage_types.includes(option.value)
                          ? 'border-orange-500 bg-orange-50'
                          : 'border-gray-300 hover:border-orange-300'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={formData.damage_types.includes(option.value)}
                        onChange={() => handleDamageTypeToggle(option.value)}
                        className="mr-2"
                      />
                      <span className="text-sm font-medium">{option.label}</span>
                    </label>
                  ))}
                </div>
                {errors.damage_types && (
                  <p className="text-red-600 text-sm mt-1 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.damage_types}
                  </p>
                )}
              </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="storm_date">Storm Date</Label>
                <Input
                  id="storm_date"
                  name="storm_date"
                  type="date"
                  value={formData.storm_date}
                  onChange={handleInputChange}
                />
              </div>

              <div>
                <Label htmlFor="inspection_date">Inspection Date</Label>
                <Input
                  id="inspection_date"
                  name="inspection_date"
                  type="date"
                  value={formData.inspection_date}
                  onChange={handleInputChange}
                />
              </div>

              <div>
                <Label htmlFor="damage_percentage">Damage Percentage</Label>
                <Input
                  id="damage_percentage"
                  name="damage_percentage"
                  type="number"
                  min="0"
                  max="100"
                  value={formData.damage_percentage}
                  onChange={handleInputChange}
                  placeholder="e.g., 35"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="damage_description">Damage Description</Label>
              <textarea
                id="damage_description"
                name="damage_description"
                value={formData.damage_description}
                onChange={handleInputChange}
                rows="3"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder="Describe the damage in detail..."
              />
            </div>
          </div>
        </div>
        )}

        {/* Storm Date for Direct Claims */}
        {isDirectClaim && (
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Calendar className="w-5 h-5 mr-2 text-orange-600" />
              Storm Information
            </h2>
            <div>
              <Label htmlFor="storm_date">Storm Date</Label>
              <Input
                id="storm_date"
                name="storm_date"
                type="date"
                value={formData.storm_date}
                onChange={handleInputChange}
                data-testid="storm-date-input"
              />
            </div>
          </div>
        )}

        {/* Photo & Video Upload */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Upload className="w-5 h-5 mr-2 text-orange-600" />
            Upload Damage Photos & Videos
          </h2>

          <BulkFileUploader
            accept="image/*,video/*"
            multiple={true}
            uploadUrl={`${BACKEND_URL}/api/claims/upload-photo`}
            folder="insurance-claims"
            label="Select Photos & Videos"
            description="Upload photos or videos of the damage. Large videos are chunked automatically."
            onFileUploaded={(result, file) => {
              setUploadedPhotos(prev => [
                ...prev,
                {
                  id: `${Date.now()}-${file.name}`,
                  file_name: result.original_filename || result.file_name || file.name,
                  file_size: file.size,
                  supabase_url: result.url,
                  supabase_path: result.key || result.path,
                }
              ]);
            }}
          />

          {/* Uploaded Photos Preview */}
          {uploadedPhotos.length > 0 && (
            <div className="space-y-2 mt-4">
              <Label>Uploaded Files ({uploadedPhotos.length})</Label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {uploadedPhotos.map(photo => (
                  <div key={photo.id} className="relative group">
                    {photo.file_name?.match(/\.(mp4|mov|avi|webm|mkv|wmv)$/i) ? (
                      <div className="w-full h-32 bg-gray-100 rounded-lg border border-gray-300 flex items-center justify-center">
                        <FileText className="w-8 h-8 text-gray-400" />
                      </div>
                    ) : (
                      <img
                        src={photo.supabase_url}
                        alt={photo.file_name}
                        className="w-full h-32 object-cover rounded-lg border border-gray-300"
                      />
                    )}
                    <button
                      type="button"
                      onClick={() => removePhoto(photo.id)}
                      className="absolute top-2 right-2 bg-red-600 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-4 h-4" />
                    </button>
                    <p className="text-xs text-gray-600 mt-1 truncate">{photo.file_name}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Lead Files — will be auto-attached to the claim */}
        {leadFiles.length > 0 && (
          <div className="bg-white p-6 rounded-lg shadow-sm border border-green-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
              <Check className="w-5 h-5 mr-2 text-green-600" />
              Files from Lead ({leadFiles.length})
            </h2>
            <p className="text-sm text-gray-600 mb-3">
              These photos, videos, and documents from the CRM lead will be automatically attached to this claim.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {leadFiles.filter(f => f.file_type === 'image').map(file => (
                <div key={file.id} className="rounded-lg overflow-hidden border border-green-200 bg-green-50">
                  <img
                    src={file.url}
                    alt={file.filename}
                    className="w-full h-24 object-cover"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <p className="text-[10px] text-gray-600 p-1 truncate">{file.filename}</p>
                </div>
              ))}
            </div>
            {leadFiles.filter(f => f.file_type !== 'image').length > 0 && (
              <div className="mt-2 space-y-1">
                {leadFiles.filter(f => f.file_type !== 'image').map(file => (
                  <div key={file.id} className="flex items-center gap-2 text-sm text-gray-700 bg-green-50 rounded px-3 py-1.5 border border-green-200">
                    <FileText className="w-4 h-4 text-green-600" />
                    <span className="truncate">{file.filename}</span>
                    <span className="text-xs text-gray-500 ml-auto">{file.file_type}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Insurance Policy Upload */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-blue-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
            <FileText className="w-5 h-5 mr-2 text-blue-600" />
            Upload Insurance Policy (Optional)
          </h2>
          <p className="text-sm text-gray-600 mb-4">
            Upload the homeowner's insurance policy to enable AI-powered policy analysis and dispute support. Accepts PDF, images, or photos of the policy.
          </p>
          <input
            id="policy_upload"
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.doc,.docx"
            multiple
            onChange={(e) => {
              const files = Array.from(e.target.files || []);
              if (files.length > 0) {
                setFormData(prev => ({ ...prev, _policyFiles: [...(prev._policyFiles || []), ...files] }));
              }
            }}
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            data-testid="policy-upload-newclaim"
          />
          {(formData._policyFiles || (formData._policyFile ? [formData._policyFile] : [])).length > 0 && (
            <div className="mt-2 space-y-1">
              {(formData._policyFiles || (formData._policyFile ? [formData._policyFile] : [])).map((f, i) => (
                <p key={i} className="text-sm text-green-600 flex items-center gap-1">
                  <Check className="w-4 h-4" />
                  {f.name} selected
                </p>
              ))}
            </div>
          )}
        </div>

        {/* Submit Buttons */}
        <div className="flex justify-end space-x-4">
          <Button
            type="button"
            onClick={() => navigate('/dashboard/claims')}
            className="px-6 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting}
            className="px-6 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Creating Claim...
              </>
            ) : (
              <>
                <Check className="w-4 h-4 mr-2" />
                Create Claim
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default NewClaim;
